import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { requestErrorMessage } from "@/lib/requestError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

function recordPath(result) {
  if (result.type === "communication") return `/communications?item=${encodeURIComponent(result.record_id)}`;
  if (result.type === "active job") return `/active-jobs/${encodeURIComponent(result.record_id)}`;
  if (result.project_id) return `/projects/${encodeURIComponent(result.project_id)}`;
  if (result.type === "project") return `/projects/${encodeURIComponent(result.record_id)}`;
  return "/permits-locates";
}

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const generation = useRef(0);
  const requestController = useRef(null);
  const composing = useRef(false);

  useEffect(() => {
    const term = query.trim();
    const current = ++generation.current;
    if (term.length < 2 || composing.current) return undefined;
    const controller = new AbortController();
    requestController.current = controller;
    const timer = window.setTimeout(async () => {
      setStatus("loading");
      try {
        const response = await axios.get(`${API}/search`, { params: { q: term }, signal: controller.signal });
        if (current !== generation.current || controller.signal.aborted) return;
        setResults(response.data.results || []);
        setStatus("ready");
      } catch (failure) {
        if (current !== generation.current || controller.signal.aborted) return;
        setError(requestErrorMessage(failure, "Search could not be loaded. Please try again."));
        setStatus("error");
      }
    }, 300);
    const requests = generation;
    return () => { window.clearTimeout(timer); controller.abort(); requests.current++; };
  }, [query, retry]);

  const changeQuery = value => {
    generation.current++;
    requestController.current?.abort();
    setQuery(value);
    setResults([]);
    setError("");
    setStatus(value.trim().length >= 2 ? "waiting" : "idle");
    setOpen(true);
  };

  return <div className="global-search" data-testid="global-search">
    <Search size={16} aria-hidden="true" />
    <input ref={inputRef} aria-label="Search JFM" data-testid="global-search-input"
      onChange={event => changeQuery(event.target.value)}
      onCompositionStart={() => { composing.current = true; requestController.current?.abort(); generation.current++; }}
      onCompositionEnd={event => { composing.current = false; changeQuery(event.currentTarget.value); setRetry(value => value + 1); }}
      onFocus={() => { setOpen(true); if (query.trim().length >= 2) setRetry(value => value + 1); }}
      onKeyDown={event => { if (event.key === "Escape") setOpen(false); }}
      placeholder="Search projects, field assignments, permits, or communications" value={query} />
    {query && <button type="button" aria-label="Clear search" onClick={() => { changeQuery(""); inputRef.current?.focus(); }}><X size={14} /></button>}
    {open && query.trim().length >= 2 && <div className="search-results" data-testid="global-search-results">
      {["waiting", "loading"].includes(status) && <p role="status">Searching…</p>}
      {status === "error" && <div role="alert"><p>{error}</p><button type="button" onClick={() => setRetry(value => value + 1)}>Try again</button></div>}
      {status === "ready" && !results.length && <p role="status">No results found.</p>}
      {status === "ready" && results.map((result, index) => <Link data-testid={`search-result-${index}`}
        key={`${result.type}-${result.record_id}`} onClick={() => setOpen(false)} to={recordPath(result)}>
        <span>{result.type === "active job" ? "field assignment" : result.type}</span><strong>{result.title}</strong><small>{result.subtitle}</small>
      </Link>)}
    </div>}
  </div>;
}
